import { useMemo } from "react";
import { useAuth } from "hooks";
import { getBrandConfig } from "constants/brands";

const LINKS = {
  admin_squads_feedback: (id) => `/squads/${id}/feedback`,
  applicant_public_link: (mission_slug, applicantType) =>
    `${location.origin}/mission_role/${mission_slug}/${applicantType?.toLocaleLowerCase()}`,
  applicant_pdf_download: (uuid) => `/mission_role_applicant/${uuid}/download`,
  applicant_preview: (uuid) => `/missions/applications/${uuid}/preview`,
  book_meeting_link: `/client/book_meeting`,
  calendly_connect: `/callbacks/calendly/connect`,
  candidate: `/candidate`,
  candidate_interview_response: (uuid) => `/candidate/interview_responses/${uuid}`,
  candidate_home: `/candidate/home`,
  candidate_profile: `/candidate/profile`,
  client_applications: "/client/applicants",
  client_faq: "https://mission.dev/build-with-mission/faq",
  client_home: "/client/home",
  client_mission_board: "/client/board",
  client_mission: (mission_slug) => `/client/board/${mission_slug}`,
  client_mission_role: (mission_slug, role_slug) => `/client/board/${mission_slug}/${role_slug}/details`,
  client_mission_role_discussions: (mission_slug, role_slug) =>
    `/client/board/${mission_slug}/${role_slug}/discussions`,
  client_mission_role_applications: (mission_uuid, role_slug) =>
    `/client/board/${mission_uuid}/${role_slug}/applications`,
  client_mission_role_vetting: (mission_slug, role_slug) => `/client/board/${mission_slug}/${role_slug}/vetting`,
  client_profile: "/client/profile",
  client_search: "/client/search",
  client_team_builder: `/client/team_builder`,
  client_signup: "/client/signup",
  signal_signup: "/signal/signup",
  community: "/community",
  coderbyte_edit: (slug) => `https://coderbyte.com/dashboard/stephanerossi-sh9iy:${slug}`,
  contact_member_experience: "https://gsquadio.atlassian.net/servicedesk/customer/portal/2/group/-1",
  dashboard: (slug) => `/dashboard/${slug}`,
  dashboard_activity_log: (slug) => `/dashboard/${slug}/activity_log`,
  dashboard_home: `/dashboard`,
  dashboard_activity_logs: `/dashboard/activity_logs`,
  direct_uploads: "/rails/active_storage/direct_uploads",
  favorites: "/favorites",
  favorite: (slug) => `/favorites/${slug}`,
  forgot_password: "/forgot_password",
  featured_teams: `/featured_teams`,
  featured_team: (slug) => `/featured_teams/${slug}`,
  home: "/home",
  insights: (profile_slug) => `${window.location.origin}/platform/profiles/${profile_slug}/insights`,
  partner_mission: (mission_slug) => `/partner/board/${mission_slug}`,
  partner_mission_board: "/partner/board",
  partner_mission_role: (mission_slug, role_slug) => `/partner/board/${mission_slug}/${role_slug}/details`,
  partner_missions: "/partner",
  library: `https://library.mission.dev`,
  list: (slug) => `/lists/${slug}`,
  lists: "/lists",
  login: "/signin",
  otp: "/otp",
  logout: "/users/sign_out",
  member_faq: "https://mission.dev/join-the-network/faq",
  freelancer_mission: (mission_slug) => `/member/board/${mission_slug}`,
  freelancer_mission_board: "/member/board",
  freelancer_mission_role: (mission_slug, role_slug) => `/member/board/${mission_slug}/${role_slug}/details`,
  freelancer_missions: "/member",
  mission: (mission_slug) => `/missions/${mission_slug}`,
  missions: "/missions",
  mission_applications: "/missions/applications",
  mission_application: (id) => `/missions/applications/${id}`,
  mission_applicants: "/missions/applicants",
  mission_board: "/missions/board",
  mission_create: `/missions/new`,
  mission_edit: (mission_slug) => `/missions/${mission_slug}/edit`,
  mission_role: (mission_slug, role_slug) => `/missions/${mission_slug}/${role_slug}`,
  mission_role_details: (mission_slug, role_slug) => `/missions/${mission_slug}/${role_slug}/details`,
  mission_role_timeline: (mission_slug, role_slug) => `/missions/${mission_slug}/${role_slug}/timeline`,
  mission_role_discussions: (mission_slug, role_slug) => `/missions/${mission_slug}/${role_slug}/discussions`,
  mission_role_recommendations: (mission_slug, role_slug) => `/missions/${mission_slug}/${role_slug}/recommendations`,
  mission_role_applications: (mission_slug, role_slug) => `/missions/${mission_slug}/${role_slug}/applications`,
  mission_role_vetting: (mission_slug, role_slug) => `/missions/${mission_slug}/${role_slug}/vetting`,
  mission_url: (mission_slug) => `/missions/${mission_slug}/proposal`,
  network: "/network",
  note: (id, noteId = null) => `/network/profiles/${id}/notes#note-${noteId}`,
  notes: (id) => `/network/profiles/${id}/notes`,
  onboarding: "/onboarding",
  organizations: "/organizations",
  organization_profile: (id) => `/organizations/${id}`,
  partners: "/partners",
  privacy: "/legal/privacy-policy",
  private_profile: (slug) => `/network/profiles/${slug}`,
  profile_code_challenges: (slug) => `/network/profiles/${slug}/code_challenges`,
  profile_video_interviews: (slug) => `/network/profiles/${slug}/video_interviews`,
  profile_video_interview_response: (slug, uuid) => `/network/profiles/${slug}/video_interviews/${uuid}`,
  profile_applications: (slug) => `/network/profiles/${slug}/applications`,
  profile_assignments: (slug) => `/network/profiles/${slug}/assignments`,
  public_list: (id) => `/lists/${id}/public`,
  public_mission_role: (mission_slug, role_slug) => `/missions/${mission_slug}/public/${role_slug}`,
  public_profile: (uuid) => `/profile/public/${uuid}`,
  pulse_survey_faq: `https://library.mission.dev/team-dynamics`,
  rate: (slug) => `/network/profiles/${slug}/rate`,
  reset_password: (id) => `/reset/${id}`,
  set_password: (id) => `/start/${id}`,
  roles: "/missions/roles",
  settings: (slug) => `/network/profiles/${slug}/edit`,
  platform_settings: "/settings",
  settings_code_challenges: "/settings/code_challenges",
  setting_mission_events: "/settings/mission_events",
  settings_video_presentations: "/settings/video_presentations",
  settings_video_presentation: (id) => `/settings/video_presentations/${id}`,
  settings_tag_manager: "/settings/tag_manager",
  settings_blueprints: "/settings/blueprints",
  settings_blueprint: (id) => `/settings/blueprints/${id}`,
  signup: "/signup", // This should be changed
  recruiter_mission: (mission_slug) => `/recruiter/board/${mission_slug}`,
  recruiter_mission_board: "/recruiter/board",
  recruiter_mission_role: (mission_slug, role_slug) => `/recruiter/board/${mission_slug}/${role_slug}/details`,
  recruiter_missions: "/recruiter",
  resend_confirmation: "/signup/send_confirmation",
  signup_with_role: (roleId) => `/signup?role=${roleId}`,
  squads: "/squads",
  squad: (id) => `/squads/${id}`,
  squad_create: `/squads/new`,
  squad_activity_log: (teamSlug) => `/squads/${teamSlug}/engineering/activity`,
  squad_activity_report: (id) => `/squads/${id}/engineering/activity`,
  squad_activity_all_time: (id) => `/squads/${id}/engineering/all_time_activity`,
  squad_daily_report: (id) => `/squads/${id}/daily_report`,
  squad_effort_report: (id) => `/squads/${id}/effort_report`,
  squad_insights: (id) => `/squads/${id}/insights`,
  squad_engineering: (id) => `/squads/${id}/engineering`,
  squad_engineering_code_telemetry_individual: (id, uuid) => `/squads/${id}/engineering/code_telemetry/${uuid}`,
  squad_profile: (id) => `/squads/${id}`,
  squad_pulse: (id) => `/squads/${id}/pulse`,
  squad_settings: (id) => `/squads/${id}/settings`,
  squad_daily_report_settings: (id) => `/squads/${id}/settings/daily_report`,
  squad_engineering_metrics_settings: (id) => `/squads/${id}/settings/engineering_metrics`,
  squad_signal_installation_settings: (id) => `/squads/${id}/settings/signal_installation`,
  squad_term_sheet: (id) => `/squads/${id}/term_sheet`,
  showcase_magic_link: (showcaseId) => `/showcase/${showcaseId}/access`,
  showcase_user_favorite_list: (showcase_slug, list_slug) => `/showcase/${showcase_slug}/lists/${list_slug}`,
  showcase_search: `/client/search`,
  showcase_signup_to_client: (uuid) => `/client/signup?showcase_id=${uuid}`,
  showcase_signup: "/showcase/signup",
  showcase_team_builder: `/client/team_builder`,
  start_impersonating: (user_id) => `/admin/start_impersonating/${user_id}`,
  stop_impersonating: `/admin/stop_impersonating`,
  terms_and_conditions: "/legal/terms-and-conditions",
  timecards: `/timecards`,
  time_sheet: "/timesheet",
  timeline: (id) => `/network/profiles/${id}/timeline`,
  website: getBrandConfig().websiteUrl,
  mission_github_app: `https://github.com/apps/mission-signal/installations/new`,
  mission_github_app_settings: (organization, installationId) =>
    `https://github.com/organizations/${organization}/settings/installations/${installationId}`,
  mission_github_staging_app: `https://github.com/apps/mission-insights-staging/installations/new`,
  github_base_url: `https://github.com`,
  github_commit_url: (repository, id) => `https://github.com/${repository}/commit/${id}`,
  github_pull_request_url: (repository, id) => `https://github.com/${repository}/pull/${id}`,
  github_review_url: (repository, prNumber, id) =>
    `https://github.com/${repository}/pull/${prNumber}#pullrequestreview-${id}`,
  github_comment_url: (repository, prNumber, id) =>
    `https://github.com/${repository}/pull/${prNumber}#discussion_r${id}`,
  github_issue_comment_url: (repository, prNumber, id) =>
    `https://github.com/${repository}/pull/${prNumber}#issuecomment-${id}`,
  user_settings: `/user/settings`,
  user_settings_account: `/user/settings/account`,
  user_settings_scheduling: `/user/settings/scheduling`,
};

/**
 * Custom hook that returns a user-type-aware version of LINKS
 * @returns {Object} Modified LINKS constant that automatically handles user type variants
 */
export default function useLinks() {
  const { isShowcase, isClient, isRecruiter, isPartner, isMember, isSignalClient, isAdmin } = useAuth();

  // Memoize the links object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // Include all base links
      ...LINKS,

      faq: isClient || isShowcase ? LINKS.client_faq : LINKS.member_faq,

      // Override mission-related links with user-type-aware versions
      mission: (...args) => {
        if (isClient || isShowcase) return LINKS.client_mission(...args);
        if (isRecruiter) return LINKS.recruiter_mission(...args);
        if (isPartner) return LINKS.partner_mission(...args);
        if (isMember) return LINKS.freelancer_mission(...args);
        return LINKS.mission(...args);
      },

      mission_role_details: (...args) => {
        if (isClient || isShowcase) return LINKS.client_mission_role(...args);
        if (isRecruiter) return LINKS.recruiter_mission_role(...args);
        if (isPartner) return LINKS.partner_mission_role(...args);
        if (isMember) return LINKS.freelancer_mission_role(...args);
        return LINKS.mission_role_details(...args);
      },

      mission_role_applications: (...args) => {
        if (isClient || isShowcase) return LINKS.client_mission_role_applications(...args);
        return LINKS.mission_role_applications(...args);
      },

      mission_role_vetting: (...args) => {
        if (isClient) return LINKS.client_mission_role_vetting(...args);
        return LINKS.mission_role_vetting(...args);
      },

      mission_board:
        [
          { check: isClient || isShowcase, link: LINKS.client_mission_board },
          { check: isRecruiter, link: LINKS.recruiter_mission_board },
          { check: isPartner, link: LINKS.partner_mission_board },
          { check: isMember, link: LINKS.freelancer_mission_board },
        ].find((entry) => entry.check)?.link || LINKS.mission_board,

      mission_role_discussions: (...args) => {
        if (isClient) return LINKS.client_mission_role_discussions(...args);
        return LINKS.mission_role_discussions(...args);
      },

      profile: (...args) => {
        if (isAdmin) return LINKS.private_profile(...args);
        return LINKS.public_profile(...args);
      },

      squad_settings: (...args) => {
        if (isSignalClient || isClient || isShowcase) return LINKS.squad_signal_installation_settings(...args);
        if (isMember) return LINKS.squad_engineering_metrics_settings(...args);
        return LINKS.squad_settings(...args);
      },
    }),
    [isClient, isShowcase, isRecruiter, isPartner, isMember, isSignalClient]
  );
}
